import { scaleUnit } from "./unitScale";

type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

type CardTheme = {
  barBackground: string;
  iconBackground: string;
  iconColor: string;
  titleColor: string;
  peakLabelColor: string;
  peakTextColor: string;
  peakDateBg: string;
  peakDateColor: string;
  peakSlotBg: string;
};

type MetricCardProps = {
  title: string;
  // Small muted text shown under the title (e.g. the date range covered).
  subtitle?: string;
  peakValue: number | null;
  averageValue: number | null;
  unit: string;
  accentColor: string;
  averageColor?: string;
  peakLabel?: string;
  averageLabel?: string;
  peakPrevDay?: number;
  averagePrevDay?: number;
  peakDate?: string;
  peakSlot?: number | null;
  peakWeather?: WeatherStats | null;
  avgWeather?: WeatherStats | null;
  showPeakWeather?: boolean;
  showWeather?: boolean;
  hideValues?: boolean;
  // Shown in place of the values row when both peak + average are null.
  emptyMessage?: string;
  theme?: Partial<CardTheme>;
  autoScaleUnit?: boolean;
  compact?: boolean;
};

function formatValue(value: number): string {
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }
  const s = value.toFixed(2);
  return s.endsWith(".00") ? String(Math.trunc(value)) : s.replace(/0$/, "");
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// slot_48 (1-48, 30-min blocks) → block start time in 24h HH:MM.
function slotToTime(slot: number | null | undefined): string | null {
  if (slot == null || slot < 1) return null;
  const mins = (slot - 1) * 30;
  const hh = String(Math.floor(mins / 60)).padStart(2, "0");
  const mm = String(mins % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function fmtW(v: number | null | undefined): string {
  if (v == null) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function getTitleIcon(title: string): React.ReactNode {
  if (title === "Energy") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    );
  }
  if (title.endsWith("Load") && !title.includes("Net")) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="white"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (title.endsWith("Generation")) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    );
  }
  if (title.endsWith("Net Load")) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

const WeatherIcon = ({ type }: { type: "temp" | "humid" | "solar" }) => {
  if (type === "temp")
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
      </svg>
    );
  if (type === "humid")
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
      </svg>
    );
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
};

const WEATHER_ITEMS: {
  label: string;
  shortLabel: string;
  iconType: "temp" | "humid" | "solar";
  key: keyof WeatherStats;
  unit: string;
}[] = [
  {
    label: "Temperature",
    shortLabel: "TEMP",
    iconType: "temp",
    key: "temp",
    unit: "°C",
  },
  {
    label: "Humidity",
    shortLabel: "HUMID",
    iconType: "humid",
    key: "humid",
    unit: "%",
  },
  {
    label: "Solar Irrad.",
    shortLabel: "SOLAR",
    iconType: "solar",
    key: "solar",
    unit: "W/m²",
  },
];

function WeatherRow({
  avgWeather,
}: {
  peakWeather?: WeatherStats | null;
  avgWeather?: WeatherStats | null;
  showPeakWeather?: boolean;
  label?: string;
}) {
  return (
    <div className="flex items-center pt-3 border-t border-[#F0F4F8]">
      {WEATHER_ITEMS.map((item) => (
        <div key={item.key} className="flex items-center gap-1 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border border-[#E8EDF2] bg-white text-[#6B7280] shadow-sm">
            <WeatherIcon type={item.iconType} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-[#6F6D7C] leading-tight">
              {fmtW(avgWeather?.[item.key])}{" "}
              <span className="text-[8px] font-semibold text-[#6B7280]">
                {item.unit}
              </span>
            </p>
            <p className="text-[8px] font-bold text-[#9098A4]  uppercase leading-tight mt-0.5">
              {item.shortLabel}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const THEME_MAP: Record<string, CardTheme> = {
  Load: {
    barBackground: "linear-gradient(180deg, #ED9D2B 0%, #D8800F 100%)",
    iconBackground: "linear-gradient(150deg, #ED9D2B 0%, #D8800F 100%)",
    iconColor: "white",
    titleColor: "#A8540B",
    peakLabelColor: "#A8540B80",
    peakTextColor: "#A8540B",
    peakDateBg: "#A8540B15",
    peakDateColor: "#A8540B",
    peakSlotBg: "#8C8A96",
  },
  Generation: {
    barBackground: "linear-gradient(180deg, #10B981 0%, #059669 100%)",
    iconBackground: "linear-gradient(150deg, #10B981 0%, #059669 100%)",
    iconColor: "white",
    titleColor: "#047857",
    peakLabelColor: "#04785780",
    peakTextColor: "#047857",
    peakDateBg: "#04785715",
    peakDateColor: "#047857",
    peakSlotBg: "#8C8A96",
  },
  "Net Load": {
    barBackground: "linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)",
    iconBackground: "linear-gradient(150deg, #8B5CF6 0%, #7C3AED 100%)",
    iconColor: "white",
    titleColor: "#7139D0",
    peakLabelColor: "#7139D080",
    peakTextColor: "#7139D0",
    peakDateBg: "#6D28D915",
    peakDateColor: "#6D28D9",
    peakSlotBg: "#8C8A96",
  },
  Energy: {
    barBackground: "linear-gradient(180deg, #94A3B8 0%, #64748B 100%)",
    iconBackground: "linear-gradient(150deg, #94A3B8 0%, #64748B 100%)",
    iconColor: "white",
    titleColor: "#475569",
    peakLabelColor: "#47556980",
    peakTextColor: "#475569",
    peakDateBg: "#47556915",
    peakDateColor: "#475569",
    peakSlotBg: "#8C8A96",
  },
  "Data Summary": {
    barBackground: "linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)",
    iconBackground: "linear-gradient(150deg, #8B5CF6 0%, #7C3AED 100%)",
    iconColor: "white",
    titleColor: "#7139D0",
    peakLabelColor: "#7139D080",
    peakTextColor: "#7139D0",
    peakDateBg: "#6D28D915",
    peakDateColor: "#6D28D9",
    peakSlotBg: "#8C8A96",
  },
};

function getFallbackTheme(accentColor: string): CardTheme {
  return {
    barBackground: accentColor,
    iconBackground: accentColor,
    iconColor: "white",
    titleColor: accentColor,
    peakLabelColor: `${accentColor}80`,
    peakTextColor: accentColor,
    peakDateBg: `${accentColor}15`,
    peakDateColor: accentColor,
    peakSlotBg: "#8C8A96",
  };
}

function resolveTheme(
  title: string,
  accentColor: string,
  theme?: Partial<CardTheme>,
): CardTheme {
  const defaultTheme = THEME_MAP[title] || getFallbackTheme(accentColor);
  return {
    ...defaultTheme,
    ...theme,
  };
}

export function MetricCard({
  title,
  subtitle,
  peakValue,
  averageValue,
  unit,
  accentColor,
  peakLabel = "Peak",
  averageLabel = "Average",
  peakPrevDay,
  averagePrevDay,
  peakDate,
  peakSlot,
  peakWeather,
  avgWeather,
  showPeakWeather = false,
  showWeather = true,
  hideValues = false,
  emptyMessage,
  theme: themeProp,
  autoScaleUnit = false,
  compact = false,
}: MetricCardProps) {
  const isEmpty =
    emptyMessage != null &&
    (peakValue == null || peakValue === 0) &&
    (averageValue == null || averageValue === 0);
  const showPrevDay = peakPrevDay !== undefined || averagePrevDay !== undefined;
  const isEnergy = peakLabel === "Total";
  const t = resolveTheme(title, accentColor, themeProp);

  const scale = (v: number | null | undefined) =>
    autoScaleUnit ? scaleUnit(v, unit) : { value: v, unit };
  const peakScaled = scale(peakValue);
  const peakPrevScaled = scale(peakPrevDay);
  const avgScaled = scale(averageValue);
  const avgPrevScaled = scale(averagePrevDay);

  const baseHex = t.peakDateBg.startsWith("#")
    ? t.peakDateBg.slice(0, 7)
    : t.peakDateBg;
  const gradientStart = `${baseHex}06`;

  const isExpanded = showPrevDay;
  const cardHeight = !showWeather ? undefined : compact ? undefined : isExpanded ? "205px" : "180px";

  return (
    <div
      className="relative rounded-[20px] border flex flex-col gap-0 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
      style={{
        background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 40%)`,
        borderColor: t.peakDateBg,
        height: cardHeight,
      }}
    >
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[4px]"
        style={{ background: t.barBackground }}
      />

      <div className={`pl-5 pr-5 flex flex-col ${compact ? "pt-2.5 pb-2.5 gap-2" : "pt-4 pb-4 gap-3.5"}`}>
        {/* Header: icon + title */}
        <div className="flex items-center gap-3">
          <div
            className={`${compact ? "w-6 h-6" : "w-7 h-7"} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
            style={{ background: t.iconBackground, color: t.iconColor }}
          >
            {getTitleIcon(title)}
          </div>
          <span
            className={`${compact ? "text-[13px]" : "text-[15px]"} font-bold tracking-tight`}
            style={{ color: t.titleColor }}
          >
            {title}
          </span>
          {subtitle && (
            <span className="text-[10px] font-medium text-[#8C8A96] leading-tight ml-auto whitespace-nowrap">
              {subtitle}
            </span>
          )}
        </div>

        {/* Values row */}
        {isEmpty ? (
          <div className="flex items-center justify-center min-h-[48px] text-[12px] font-medium text-[#9CA3AF] text-center">
            {emptyMessage}
          </div>
        ) : !hideValues ? (
        <div className="flex items-start gap-0">
          {/* Peak / Total column */}
          <div className="flex-[1.4] min-w-0">
            <div className={`flex items-center gap-1.5 ${compact ? "mb-1 min-h-[18px]" : "mb-1.5 min-h-[24px]"}`}>
              <p
                className="text-[10px] font-bold uppercase "
                style={{ color: t.peakLabelColor }}
              >
                {peakLabel}
              </p>
              {!isEnergy && peakDate && (
                <>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-md border border-[#E6E9EF] bg-[#f6f7fa] text-[#6E7783] whitespace-nowrap">
                    {formatDate(peakDate)}
                  </span>
                  {peakSlot != null && slotToTime(peakSlot) && (
                    <span className="text-[10px] font-medium text-white px-1.5 py-0.5 rounded-[6px] bg-[#A9A8B1] whitespace-nowrap">
                      {slotToTime(peakSlot)}
                    </span>
                  )}
                </>
              )}
            </div>
            <p
              className={`${compact ? "text-[17px]" : "text-[22px]"} font-bold leading-tight tracking-tight`}
              style={{ color: t.peakTextColor }}
            >
              {peakValue == null ? (
                <span className="text-[#A3AED0]">—</span>
              ) : (
                <>
                  {formatValue(peakScaled.value as number)}{" "}
                  <span className="text-[11px] font-bold text-[#8C8A96] ml-1">
                    {peakScaled.unit}
                  </span>
                </>
              )}
            </p>
            {showPrevDay &&
              peakPrevDay !== undefined &&
              peakValue != null &&
              (() => {
                const diff = peakPrevDay - peakValue;
                const color =
                  diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
                const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                return (
                  <p
                    className="text-[10px] font-semibold leading-tight flex items-center gap-0.5 mt-1"
                    style={{ color }}
                  >
                    {arrow && <span className="text-[8px]">{arrow}</span>}
                    {formatValue(peakPrevScaled.value as number)}{" "}
                    <span className="text-[8.5] font-normal text-[#8C8A96]">
                      prev day
                    </span>
                  </p>
                );
              })()}
          </div>

          {/* Divider */}
          <div className="w-px bg-[#E8EDF2] mx-4 self-stretch" />

          {/* Average column */}
          <div className="flex-[0.8] min-w-0">
            <div className={`flex items-center ${compact ? "mb-1 min-h-[18px]" : "mb-1.5 min-h-[24px]"}`}>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase ">
                {averageLabel}
              </p>
            </div>
            <p className={`${compact ? "text-[13px]" : "text-[15px]"} font-semibold text-[#6F6D7C] leading-tight tracking-tight`}>
              {averageValue == null ? (
                <span className="text-[#A3AED0]">—</span>
              ) : (
                <>
                  {formatValue(avgScaled.value as number)}{" "}
                  <span className="text-[10px] font-bold text-[#8C8A96] ml-1">
                    {avgScaled.unit}
                  </span>
                </>
              )}
            </p>
            {showPrevDay &&
              averagePrevDay !== undefined &&
              averageValue != null &&
              (() => {
                const diff = averagePrevDay - averageValue;
                const color =
                  diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
                const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                return (
                  <p
                    className="text-[10px] font-semibold leading-tight flex items-center gap-0.5 mt-1"
                    style={{ color }}
                  >
                    {arrow && <span className="text-[8px]">{arrow}</span>}
                    {formatValue(avgPrevScaled.value as number)}{" "}
                    <span className="text-[8.5] font-normal text-[#8C8A96]">
                      prev day
                    </span>
                  </p>
                );
              })()}
          </div>
        </div>
        ) : null}

        {/* Weather row */}
        {(showWeather || hideValues) && (
          <WeatherRow
            peakWeather={peakWeather}
            avgWeather={avgWeather}
            showPeakWeather={showPeakWeather}
          />
        )}
      </div>
    </div>
  );
}

// Weather card — TEMP / HUMID / SOLAR shown as 3 big-value columns (same
// PEAK/AVERAGE layout as MetricCard) instead of the compact icon row.
export function WeatherCard({
  temp,
  humid,
  solar,
  title = "Weather",
  theme: themeProp,
  compact = false,
}: {
  temp: number | null;
  humid: number | null;
  solar: number | null;
  title?: string;
  theme?: Partial<CardTheme>;
  compact?: boolean;
}) {
  const t = resolveTheme(title, "#8B5CF6", themeProp);
  const baseHex = t.peakDateBg.startsWith("#")
    ? t.peakDateBg.slice(0, 7)
    : t.peakDateBg;
  const gradientStart = `${baseHex}06`;

  const cols: { label: string; value: number | null; unit: string }[] = [
    { label: "Temp", value: temp, unit: "°C" },
    { label: "Humid", value: humid, unit: "%" },
    { label: "Solar", value: solar, unit: "W/m²" },
  ];

  return (
    <div
      className="relative rounded-[20px] border flex flex-col gap-0 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
      style={{
        background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 40%)`,
        borderColor: t.peakDateBg,
      }}
    >
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[4px]"
        style={{ background: t.barBackground }}
      />

      <div className={`pl-5 pr-5 flex flex-col ${compact ? "pt-2.5 pb-2.5 gap-2" : "pt-4 pb-4 gap-3.5"}`}>
        {/* Header: icon + title */}
        <div className="flex items-center gap-3">
          <div
            className={`${compact ? "w-6 h-6" : "w-7 h-7"} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
            style={{ background: t.iconBackground, color: t.iconColor }}
          >
            <WeatherIcon type="temp" />
          </div>
          <span
            className={`${compact ? "text-[13px]" : "text-[15px]"} font-bold tracking-tight`}
            style={{ color: t.titleColor }}
          >
            {title}
          </span>
        </div>

        {/* Values row: TEMP / HUMID / SOLAR */}
        <div className="flex items-start">
          {cols.map((c, i) => (
            <div key={c.label} className="flex items-stretch flex-1 min-w-0">
              {i > 0 && <div className="w-px bg-[#E8EDF2] mx-4 self-stretch" />}
              <div className="flex-1 min-w-0">
                <div className={`flex items-center ${compact ? "mb-1 min-h-[18px]" : "mb-1.5 min-h-[24px]"}`}>
                  <p className="text-[10px] font-bold text-[#9CA3AF] uppercase">
                    {c.label}
                  </p>
                </div>
                <p
                  className={`${compact ? "text-[15px]" : "text-[18px]"} font-bold leading-tight tracking-tight`}
                  style={{ color: t.peakTextColor }}
                >
                  {c.value == null ? (
                    <span className="text-[#A3AED0]">—</span>
                  ) : (
                    <>
                      {formatValue(c.value)}{" "}
                      <span className="text-[10px] font-bold text-[#8C8A96] ml-1">
                        {c.unit}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type DataSummaryCardProps = {
  availableValue: number | null;
  zerosValue: number | null;
  nullsValue?: number | null;
  title?: string;
  theme?: Partial<CardTheme>;
  showBreakdown?: boolean;
  compact?: boolean;
};

export function DataSummaryCard({
  availableValue,
  zerosValue,
  nullsValue,
  title = "Data Summary",
  theme: themeProp,
  showBreakdown = true,
  compact = false,
}: DataSummaryCardProps) {
  const t = resolveTheme(title, "#8B5CF6", themeProp);
  const baseHex = t.peakDateBg.startsWith("#")
    ? t.peakDateBg.slice(0, 7)
    : t.peakDateBg;
  const gradientStart = `${baseHex}06`;

  const availPct =
    availableValue != null ? Number(availableValue.toFixed(2)) : null;
  const zerosPct = zerosValue != null ? Number(zerosValue.toFixed(2)) : null;

  // Calculate nulls / missing as fallback if not provided
  const nullsPct =
    nullsValue != null
      ? Number(nullsValue.toFixed(2))
      : availPct != null && zerosPct != null
        ? Number(Math.max(0, 100 - availPct - zerosPct).toFixed(2))
        : null;

  return (
    <div
      className="relative rounded-[20px] border flex flex-col gap-0 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
      style={{
        background: `linear-gradient(135deg, ${gradientStart} 0%, #ffffff 40%)`,
        borderColor: t.peakDateBg,
        height: showBreakdown ? "180px" : undefined,
      }}
    >
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[4px]"
        style={{ background: t.barBackground }}
      />

      <div className={`pl-5 pr-5 flex flex-col h-full justify-between ${compact ? "pt-2.5 pb-2.5 gap-2" : "pt-4 pb-4 gap-3.5"}`}>
        <div className={`flex flex-col ${compact ? "gap-2" : "gap-3.5"}`}>
          {/* Header: icon + title */}
          <div className="flex items-center gap-3">
            <div
              className={`${compact ? "w-6 h-6" : "w-7 h-7"} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
              style={{ background: t.iconBackground, color: t.iconColor }}
            >
              {/* Shield Check Icon */}
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <span
              className={`${compact ? "text-[13px]" : "text-[15px]"} font-bold tracking-tight`}
              style={{ color: t.titleColor }}
            >
              {title}
            </span>
          </div>

          {/* Values row */}
          <div className="flex items-start gap-0">
            {/* Availability column */}
            <div className="flex-[1.4] min-w-0">
              <div className={`flex items-center gap-1.5 ${compact ? "mb-1 min-h-[18px]" : "mb-1.5 min-h-[24px]"}`}>
                <p
                  className="text-[10px] font-bold uppercase "
                  style={{ color: t.peakLabelColor }}
                >
                  Available
                </p>
              </div>
              <p
                className={`${compact ? "text-[17px]" : "text-[22px]"} font-bold leading-tight tracking-tight`}
                style={{ color: t.peakTextColor }}
              >
                {availPct == null ? (
                  <span className="text-[#A3AED0]">—</span>
                ) : (
                  <>
                    {availPct}{" "}
                    <span className="text-[11px] font-bold text-[#8C8A96] ml-1">
                      %
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Divider */}
            <div className="w-px bg-[#E8EDF2] mx-4 self-stretch" />

            {/* Zeroes column */}
            <div className="flex-[0.8] min-w-0">
              <div className={`flex items-center ${compact ? "mb-1 min-h-[18px]" : "mb-1.5 min-h-[24px]"}`}>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase ">
                  Zeroes
                </p>
              </div>
              <p className={`${compact ? "text-[13px]" : "text-[15px]"} font-semibold text-[#6F6D7C] leading-tight tracking-tight mt-[3px]`}>
                {zerosPct == null ? (
                  <span className="text-[#A3AED0]">—</span>
                ) : (
                  <>
                    {zerosPct}{" "}
                    <span className="text-[10px] font-bold text-[#8C8A96] ml-1">
                      %
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Stacked Quality Bar / breakdown */}
        {showBreakdown && (
        <div className="flex flex-col gap-1.5 border-t border-[#F0F4F8]">
          <div className="h-[7px] w-full rounded-full bg-[#E5E7EB] flex overflow-hidden">
            {availPct != null && availPct > 0 && (
              <div
                className="bg-[#10B981] h-full transition-all duration-500"
                style={{ width: `${availPct}%` }}
                title={`Available: ${availPct}%`}
              />
            )}
            {zerosPct != null && zerosPct > 0 && (
              <div
                className="bg-[#F59E0B] h-full transition-all duration-500"
                style={{ width: `${zerosPct}%` }}
                title={`Zeroes: ${zerosPct}%`}
              />
            )}
            {nullsPct != null && nullsPct > 0 && (
              <div
                className="bg-[#EF4444] h-full transition-all duration-500"
                style={{ width: `${nullsPct}%` }}
                title={`Missing: ${nullsPct}%`}
              />
            )}
          </div>
          <div className="flex justify-between text-[11px] font-bold text-[#8C8A96] leading-tight mt-0.5">
            <span className="flex items-center gap-1">
              <span className="w-[6px] h-[6px] rounded-full bg-[#10B981]" />
              {availPct != null ? `${availPct}%` : "—"} Avail
            </span>
            <span className="flex items-center gap-1">
              <span className="w-[6px] h-[6px] rounded-full bg-[#F59E0B]" />
              {zerosPct != null ? `${zerosPct}%` : "—"} Zero
            </span>
            <span className="flex items-center gap-1">
              <span className="w-[6px] h-[6px] rounded-full bg-[#EF4444]" />
              {nullsPct != null ? `${nullsPct}%` : "—"} Miss
            </span>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
